import { create } from "zustand";
import { Conversation } from "../api/models/Conversation";
import { Message } from "../api/models/Message";

type DeltaMap = Record<number, string>;

/** The server publishes a function call before the final assistant message is
 * durable. Keep those calls with the in-flight response so tool activity is
 * visible immediately instead of being relegated to a transient toast. */
interface LiveToolCall {
  id: string;
  type: "function";
  name: string;
  args: unknown;
  output?: string;
}

/** OpenAI starts a fresh response after a tool round-trip, so do not infer a
 * block boundary from its sequence numbers (which may also arrive out of order). */
interface StreamState {
  completedTextBlocks: string[];
  completedReasoningBlocks: string[];
  textDeltas: DeltaMap;
  reasoningText: string;
  reasoningBlockId?: string;
  toolCalls: LiveToolCall[];
}

const emptyStream = (): StreamState => ({ completedTextBlocks: [], completedReasoningBlocks: [], textDeltas: {}, reasoningText: "", toolCalls: [] });
const orderedText = (deltas: DeltaMap) => Object.keys(deltas).map(Number).sort((a, b) => a - b).map((sequence) => deltas[sequence]).join("");
const streamText = (blocks: string[], deltas: DeltaMap) => [...blocks, orderedText(deltas)].filter(Boolean).join("\n\n");
const cloneMessage = (message: Message): Message => ({ ...message, content: message.content?.map((content) => ({ ...content })) });

const withInFlightMessage = (conversation: Conversation, stream: StreamState): Conversation => {
  const messages = [...(conversation.messages || [])].map(cloneMessage);
  const text = streamText(stream.completedTextBlocks, stream.textDeltas);
  const thinkingSummary = [...stream.completedReasoningBlocks, stream.reasoningText].filter(Boolean).join("\n\n");
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.id === "in-flight-message") {
    lastMessage.content = [{ ...(lastMessage.content?.[0] || {}), text }];
    lastMessage.thinkingSummary = thinkingSummary || undefined;
    lastMessage.runDetails = stream.toolCalls.map((call) => ({ ...call }));
  } else {
    messages.push({
      id: "in-flight-message",
      role: Message.RoleEnum.Assistant,
      content: [{ text, fileCitations: [], images: [] }],
      thinkingSummary: thinkingSummary || undefined,
      runDetails: stream.toolCalls.map((call) => ({ ...call })),
    });
  }
  return { ...conversation, messages };
};

const pendingMessages = (conversation?: Conversation) => (conversation?.messages || []).filter((message) => message.id?.startsWith("temp-"));
const appendPending = (serverConversation: Conversation, current?: Conversation): Conversation => ({
  ...serverConversation,
  messages: [...(serverConversation.messages || []), ...pendingMessages(current).filter((message) =>
    !(serverConversation.messages || []).some((serverMessage) => serverMessage.role === message.role && serverMessage.content?.[0]?.text === message.content?.[0]?.text),
  )],
});

export interface ConversationState {
  currentConversation: Conversation | undefined;
  conversations: Conversation[];
  messageInProgress: boolean;
  streams: Record<string, StreamState>;
  pendingResponses: Record<string, true>;
  isConversationInProgress: (conversationId: string | undefined) => boolean;
  setCurrentConversation: (conversation: Conversation | undefined) => void;
  setConversations: (conversations: Conversation[]) => void;
  setMessageInProgress: (inProgress: boolean) => void;
  addOptimisticMessage: (conversationId: string, message: Message) => void;
  confirmOptimisticMessage: (conversationId: string, temporaryId: string, messageId: string) => void;
  removeOptimisticMessage: (conversationId: string, temporaryId: string) => void;
  beginResponse: (conversationId: string) => void;
  applyMessageDelta: (conversationId: string, sequenceNumber: number, delta: string) => void;
  applyReasoningDelta: (conversationId: string, sequenceNumber: number, delta: string, blockId?: string) => void;
  applyFunctionCall: (conversationId: string, name: string, args: unknown, result?: string) => void;
  completeStreamLocally: (conversationId: string) => void;
  completeStream: (conversationId: string, serverConversation: Conversation) => void;
  failStream: (conversationId: string) => void;
  mergeServerConversation: (serverConversation: Conversation) => void;
  updateConversationName: (id: string, name: string) => void;
  updateConversationShareLink: (id: string, shareLink: string | undefined) => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  currentConversation: undefined,
  conversations: [],
  messageInProgress: false,
  streams: {},
  pendingResponses: {},
  isConversationInProgress: (conversationId) => Boolean(conversationId && get().pendingResponses[conversationId]),
  setCurrentConversation: (conversation) => set({ currentConversation: conversation }),
  setConversations: (conversations) => set({ conversations }),
  setMessageInProgress: (inProgress) => set({ messageInProgress: inProgress }),

  addOptimisticMessage: (conversationId, message) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    const pendingResponses = { ...get().pendingResponses, [conversationId]: true as const };
    set({ currentConversation: { ...current, messages: [...(current.messages || []), message] }, pendingResponses, messageInProgress: Object.keys(pendingResponses).length > 0 });
  },
  confirmOptimisticMessage: (conversationId, temporaryId, messageId) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    set({ currentConversation: { ...current, messages: (current.messages || []).map((message) => message.id === temporaryId ? { ...message, id: messageId } : message) } });
  },
  removeOptimisticMessage: (conversationId, temporaryId) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    const pendingResponses = { ...get().pendingResponses };
    delete pendingResponses[conversationId];
    set({ currentConversation: { ...current, messages: (current.messages || []).filter((message) => message.id !== temporaryId) }, pendingResponses, messageInProgress: Object.keys(pendingResponses).length > 0 });
  },

  beginResponse: (conversationId) => {
    const previous = get().streams[conversationId] || emptyStream();
    const text = orderedText(previous.textDeltas);
    const next: StreamState = {
      completedTextBlocks: text ? [...previous.completedTextBlocks, text] : previous.completedTextBlocks,
      completedReasoningBlocks: previous.reasoningText ? [...previous.completedReasoningBlocks, previous.reasoningText] : previous.completedReasoningBlocks,
      textDeltas: {}, reasoningText: "", reasoningBlockId: undefined, toolCalls: previous.toolCalls,
    };
    const streams = { ...get().streams, [conversationId]: next };
    const pendingResponses = { ...get().pendingResponses, [conversationId]: true as const };
    const current = get().currentConversation;
    set({ streams, pendingResponses, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  applyMessageDelta: (conversationId, sequenceNumber, delta) => {
    const previous = get().streams[conversationId] || emptyStream();
    const next = { ...previous, textDeltas: { ...previous.textDeltas, [sequenceNumber]: delta } };
    const streams = { ...get().streams, [conversationId]: next };
    const pendingResponses = { ...get().pendingResponses, [conversationId]: true as const };
    const current = get().currentConversation;
    set({ streams, pendingResponses, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  applyReasoningDelta: (conversationId, _sequenceNumber, delta, blockId) => {
    const previous = get().streams[conversationId] || emptyStream();
    // Reasoning summary sequence numbers restart for each summary part. The
    // websocket is ordered, and the backend carries OpenAI's stable part ID,
    // so append deltas within a part and add a Markdown paragraph boundary
    // only when a new summary part starts.
    const startsNewPart = Boolean(blockId && previous.reasoningBlockId && blockId !== previous.reasoningBlockId);
    const next = {
      ...previous,
      completedReasoningBlocks: startsNewPart && previous.reasoningText
        ? [...previous.completedReasoningBlocks, previous.reasoningText]
        : previous.completedReasoningBlocks,
      reasoningText: startsNewPart ? delta : previous.reasoningText + delta,
      reasoningBlockId: blockId || previous.reasoningBlockId,
    };
    const streams = { ...get().streams, [conversationId]: next };
    const pendingResponses = { ...get().pendingResponses, [conversationId]: true as const };
    const current = get().currentConversation;
    set({ streams, pendingResponses, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  applyFunctionCall: (conversationId, name, args, result) => {
    const previous = get().streams[conversationId] || emptyStream();
    const next = {
      ...previous,
      toolCalls: [...previous.toolCalls, {
        id: `live-tool-${previous.toolCalls.length}-${Date.now()}`,
        type: "function" as const,
        name,
        args,
        output: result,
      }],
    };
    const streams = { ...get().streams, [conversationId]: next };
    const pendingResponses = { ...get().pendingResponses, [conversationId]: true as const };
    const current = get().currentConversation;
    set({ streams, pendingResponses, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  completeStreamLocally: (conversationId) => {
    const streams = { ...get().streams };
    delete streams[conversationId];
    const pendingResponses = { ...get().pendingResponses };
    delete pendingResponses[conversationId];
    const current = get().currentConversation;
    const messages = current?.id === conversationId ? (current.messages || []).map((message) =>
      message.id === "in-flight-message" ? { ...message, id: `local-response-${Date.now()}` } : message,
    ) : current?.messages;
    set({
      streams,
      pendingResponses,
      messageInProgress: Object.keys(pendingResponses).length > 0,
      currentConversation: current?.id === conversationId ? { ...current, messages } : current,
    });
  },
  completeStream: (conversationId, serverConversation) => {
    const streams = { ...get().streams };
    delete streams[conversationId];
    const pendingResponses = { ...get().pendingResponses };
    delete pendingResponses[conversationId];
    const current = get().currentConversation;
    set({ streams, pendingResponses, messageInProgress: Object.keys(pendingResponses).length > 0, currentConversation: current?.id === conversationId ? appendPending(serverConversation, current) : current });
  },
  failStream: (conversationId) => {
    const streams = { ...get().streams };
    delete streams[conversationId];
    const pendingResponses = { ...get().pendingResponses };
    delete pendingResponses[conversationId];
    const current = get().currentConversation;
    set({
      streams,
      pendingResponses,
      messageInProgress: Object.keys(pendingResponses).length > 0,
      currentConversation: current?.id === conversationId
        ? { ...current, messages: (current.messages || []).filter((message) => message.id !== "in-flight-message") }
        : current,
    });
  },
  // A non-terminal refresh is allowed to update metadata, but never erase a local stream.
  mergeServerConversation: (serverConversation) => {
    const current = get().currentConversation;
    if (!current || current.id !== serverConversation.id) return;
    const stream = get().streams[serverConversation.id];
    const base = appendPending(serverConversation, current);
    set({ currentConversation: stream ? withInFlightMessage(base, stream) : base });
  },
  updateConversationName: (id, name) => set((state) => ({
    conversations: state.conversations.map((conversation) => conversation.id === id ? { ...conversation, name } : conversation),
    currentConversation: state.currentConversation?.id === id ? { ...state.currentConversation, name } : state.currentConversation,
  })),
  updateConversationShareLink: (id, shareLink) => set((state) => ({
    conversations: state.conversations.map((conversation) => conversation.id === id ? { ...conversation, shareLink } : conversation),
    currentConversation: state.currentConversation?.id === id ? { ...state.currentConversation, shareLink } : state.currentConversation,
  })),
}));
